import { useState, useCallback, useMemo } from "react";
import type { Model } from "@bryntum/schedulerpro";
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
  Check,
  X,
  RefreshCw,
  ActivitySquare,
  Sparkles,
  Loader2,
} from "lucide-react";
import { MarkdownRenderer } from "@/modern/components/ui/markdown-renderer";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { useBoardContextContext } from "../context/BoardContextContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { showToast } from "@/other/utils/utils.jsx";
import type { BoardContextGroup } from "@/bryntum-lib/models/BoardContextGroup";
import type { BoardContextStatement } from "@/bryntum-lib/models/BoardContextStatement";
import { useConfirm } from "@omit/react-confirm-dialog";

const MARKDOWN_STRIP_RE = /[#*_`~>-]/g;

/** Shared preview styling — soft surface + orange accent (light cream / dark neutral). */
const previewStatementSurface = cn(
  "rounded-lg border border-border/60 bg-orange-50/70 dark:bg-card",
  "border-l-[3px] border-l-dashed border-l-orange-500",
  "shadow-sm"
);

const AiSuggestedBadge = () => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-md px-2 py-0.5 shrink-0",
      "text-[10px] font-bold uppercase tracking-wide",
      "bg-orange-500/10 text-orange-700 dark:text-orange-400",
      "ring-1 ring-orange-500/15"
    )}
  >
    <Sparkles className="h-2.5 w-2.5" aria-hidden />
    AI Suggested
  </span>
);

// ── Inline statement editor ───────────────────────────────────────────────────
interface StatementCardProps {
  statement: BoardContextStatement;
  group?: BoardContextGroup;
  groupStatementCount?: number;
  onSaved: () => void;
}

const StatementCard = ({ statement, group, groupStatementCount = 0, onSaved }: StatementCardProps) => {
  const { project } = useSwivuBryntumContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [headline, setHeadline] = useState(statement.headline ?? "");
  const [body, setBody] = useState(statement.body);
  const confirm = useConfirm();
  const isPreview = Boolean(statement.isPreview);

  const handleEdit = () => {
    setHeadline(statement.headline ?? "");
    setBody(statement.body);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setHeadline(statement.headline ?? "");
    setBody(statement.body);
  };

  const acceptPreviewStatement = (fields?: { headline: string | null; body: string }) => {
    statement.set({
      ...(fields ?? {}),
      isPreview: false,
    });
    if (group?.isPreview) {
      group.set({ isPreview: false });
    }
  };

  const handleSave = async () => {
    if (!project || !body.trim()) return;
    setIsSaving(true);
    try {
      statement.set({
        headline: headline.trim() || null,
        body: body.trim(),
      });
      await project.sync();
      setIsEditing(false);
      onSaved();
    } catch {
      showToast({ msg: "Failed to save statement.", type: "error", duration: 3000 });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAccept = async () => {
    if (!project) return;
    setIsAccepting(true);
    try {
      acceptPreviewStatement();
      await project.sync();
    } catch {
      showToast({ msg: "Failed to accept suggestion.", type: "error", duration: 3000 });
    } finally {
      setIsAccepting(false);
    }
  };

  const isLastStatementInPreviewGroup =
    Boolean(group?.isPreview) && groupStatementCount === 1;

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Delete statement",
      description: isLastStatementInPreviewGroup
        ? "This AI suggestion and its group will be removed."
        : isPreview
          ? "This AI suggestion will be removed."
          : "This statement will be permanently removed from the context.",
      customActions: ({ confirm: proceed, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="space-x-3">
          <Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button>
          <Button variant="destructive" size="sm" onClick={proceed}>Delete</Button>
        </div>
      ),
    });
    if (!confirmed) return;

    if (!project) return;
    setIsDeleting(true);
    try {
      const stmtStore = project.getCrudStore(STORE_KEYS.boardContextStatementStore);
      stmtStore?.remove(statement as unknown as Model);
      if (isLastStatementInPreviewGroup && group) {
        const grpStore = project.getCrudStore(STORE_KEYS.boardContextGroupStore);
        grpStore?.remove(group as unknown as Model);
      }
      await project.sync();
    } catch {
      showToast({ msg: "Failed to delete statement.", type: "error", duration: 3000 });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isEditing) {
    return (
      <div
        className={cn(
          "rounded-lg border p-4 space-y-3",
          isPreview
            ? cn(previewStatementSurface, "p-4")
            : "border-primary/30 bg-primary/[0.02]"
        )}
      >
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Headline <span className="normal-case font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="Short headline…"
            disabled={isSaving}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={isSaving}
            rows={4}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !body.trim()}
            className="gap-1.5 h-7 text-xs"
          >
            <Check className="h-3 w-3" />
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCancel}
            disabled={isSaving}
            className="gap-1.5 h-7 text-xs text-muted-foreground"
          >
            <X className="h-3 w-3" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (isPreview) {
    return (
      <div className={cn(previewStatementSurface, "px-4 py-3.5 space-y-2.5")}>
        <div className="flex flex-wrap items-center gap-2 gap-y-1">
          <AiSuggestedBadge />
          {statement.headline && (
            <p className="text-sm font-semibold text-foreground leading-snug">{statement.headline}</p>
          )}
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">{statement.body}</p>
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          <Button
            size="sm"
            onClick={handleAccept}
            disabled={isAccepting}
            className="h-8 gap-1.5 text-xs bg-orange-600 hover:bg-orange-700 text-white shadow-none"
          >
            {isAccepting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Accept
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleEdit}
            className="h-8 gap-1.5 text-xs border-border bg-background text-foreground"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDelete}
            disabled={isDeleting}
            className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative rounded-lg border border-border bg-card px-4 py-3 hover:border-border/80 hover:shadow-sm transition-all duration-150">
      {statement.headline && (
        <p className="text-xs font-semibold text-primary/80 uppercase tracking-wide mb-1">
          {statement.headline}
        </p>
      )}
      <p className="text-sm text-foreground/90 leading-relaxed">{statement.body}</p>

      <div className="absolute top-2.5 right-2.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-100">
        <button
          type="button"
          onClick={handleEdit}
          title="Edit"
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        >
          <Pencil className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={isDeleting}
          title="Delete"
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-40"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
};

// ── Group section ─────────────────────────────────────────────────────────────
interface GroupSectionProps {
  group: BoardContextGroup;
  statements: BoardContextStatement[];
}

const GroupSection = ({ group, statements }: GroupSectionProps) => {
  const { project } = useSwivuBryntumContext();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(group.title);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isAcceptingGroup, setIsAcceptingGroup] = useState(false);
  const confirm = useConfirm();
  const isPreviewGroup = Boolean(group.isPreview);

  const groupStatements = statements.filter((s) => s.group === group.id);

  const handleSaveTitle = async () => {
    if (!project || !titleValue.trim()) return;
    setIsSavingTitle(true);
    try {
      group.set({ title: titleValue.trim() });
      await project.sync();
      setIsEditingTitle(false);
    } catch {
      showToast({ msg: "Failed to save group title.", type: "error", duration: 3000 });
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleAcceptGroup = async () => {
    if (!project) return;
    setIsAcceptingGroup(true);
    try {
      group.set({ isPreview: false });
      for (const s of groupStatements) {
        if (s.isPreview) s.set({ isPreview: false });
      }
      await project.sync();
    } catch {
      showToast({ msg: "Failed to accept group.", type: "error", duration: 3000 });
    } finally {
      setIsAcceptingGroup(false);
    }
  };

  const handleDeleteGroup = async () => {
    const confirmed = await confirm({
      title: "Delete group",
      description: `"${group.title}" and all its statements will be removed.`,
      customActions: ({ confirm: proceed, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="space-x-3">
          <Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button>
          <Button variant="destructive" size="sm" onClick={proceed}>Delete group</Button>
        </div>
      ),
    });
    if (!confirmed || !project) return;

    try {
      const stmtStore = project.getCrudStore(STORE_KEYS.boardContextStatementStore);
      stmtStore?.remove(groupStatements as unknown as Model[]);
      const grpStore = project.getCrudStore(STORE_KEYS.boardContextGroupStore);
      grpStore?.remove(group as unknown as Model);
      await project.sync();
    } catch {
      showToast({ msg: "Failed to delete group.", type: "error", duration: 3000 });
    }
  };

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-background overflow-hidden",
        isPreviewGroup && "ring-1 ring-orange-500/10"
      )}
    >
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        {isEditingTitle ? (
          <div className="flex items-center gap-2 flex-1">
            <input
              type="text"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveTitle();
                if (e.key === "Escape") { setIsEditingTitle(false); setTitleValue(group.title); }
              }}
              disabled={isSavingTitle}
              className="flex-1 rounded border border-primary/40 bg-background px-2 py-0.5 text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <button
              type="button"
              onClick={handleSaveTitle}
              disabled={isSavingTitle}
              className="text-primary hover:text-primary/80"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => { setIsEditingTitle(false); setTitleValue(group.title); }}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0 group/title">
            {isPreviewGroup && (
              <Sparkles className="h-3.5 w-3.5 text-orange-500 shrink-0" aria-hidden />
            )}
            <h3 className="text-sm font-semibold text-foreground flex-1 truncate">{group.title}</h3>
            <div className="flex items-center gap-1 opacity-0 group-hover/title:opacity-100 transition-opacity shrink-0">
              {isPreviewGroup && (
                <button
                  type="button"
                  onClick={handleAcceptGroup}
                  disabled={isAcceptingGroup}
                  title="Accept all in group"
                  className="h-6 px-2 flex items-center justify-center gap-1 rounded-md text-[10px] font-medium bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                >
                  {isAcceptingGroup ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Check className="h-3 w-3" />
                  )}
                  Accept
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsEditingTitle(true)}
                className="h-5 w-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                title="Rename group"
              >
                <Pencil className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={handleDeleteGroup}
                className="h-5 w-5 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                title="Delete group"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}

        <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
          {groupStatements.length}
        </span>
      </div>

      {isExpanded && (
        <div className={cn("p-3 space-y-2.5", isPreviewGroup && "bg-muted/20 dark:bg-muted/10")}>
          {groupStatements.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4 italic">
              No statements in this group.
            </p>
          ) : (
            groupStatements.map((s) => (
              <StatementCard
                key={s.id}
                statement={s}
                group={group}
                groupStatementCount={groupStatements.length}
                onSaved={() => {}}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ── Context health band ───────────────────────────────────────────────────────
const ContextHealthBand = ({ markdown }: { markdown: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const stripped = markdown.replace(MARKDOWN_STRIP_RE, "").replace(/\s+/g, " ").trim();
  const preview = stripped.slice(0, 320);

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/[0.03]">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-start gap-2.5 px-4 py-3 text-left"
      >
        <ActivitySquare className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold text-primary">Context Health</span>
          {!isExpanded && (
            <p className="text-xs text-muted-foreground/70 mt-0.5 line-clamp-3 leading-relaxed">{preview}{preview.length < stripped.length ? "…" : ""}</p>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-primary/60 transition-transform duration-200 shrink-0 mt-0.5",
            isExpanded && "rotate-180"
          )}
        />
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-primary/10">
          <MarkdownRenderer content={markdown} className="text-foreground/80 pt-3" />
        </div>
      )}
    </div>
  );
};

// ── Main context view ─────────────────────────────────────────────────────────
export const ContextView = () => {
  const {
    groups,
    statements,
    boardContextRecord,
    handleRegenerate,
    handleGenerateSuggestions,
    isGeneratingSuggestions,
    hasOpenAIKey,
  } = useBoardContextContext();
  const confirm = useConfirm();

  const sortedGroups = useMemo(
    () =>
      [...groups].sort((a, b) => {
        if (a.isPreview !== b.isPreview) return a.isPreview ? 1 : -1;
        return a.order - b.order;
      }),
    [groups]
  );

  const onRegenerate = useCallback(async () => {
    const confirmed = await confirm({
      title: "Update sources",
      description:
        "Editing your URL, description, or document will clear the current questions phase and re-run the full analysis. Your existing context will remain visible until a new one is generated.",
      customActions: ({ confirm: proceed, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="space-x-3">
          <Button variant="ghost" size="sm" onClick={cancel}>Keep current</Button>
          <Button size="sm" onClick={proceed}>Update sources</Button>
        </div>
      ),
    });
    if (confirmed) handleRegenerate();
  }, [confirm, handleRegenerate]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="shrink-0 px-6 pt-6 pb-4 border-b border-border flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">Business Context</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            AI-generated from your sources. Edit any statement inline — changes sync automatically.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <Button
            size="sm"
            onClick={() => void handleGenerateSuggestions()}
            disabled={!hasOpenAIKey || isGeneratingSuggestions}
            title={!hasOpenAIKey ? "An OpenAI API key is required" : undefined}
            className={cn(
              "gap-1.5 text-xs shadow-none border-0",
              "bg-violet-600 hover:bg-violet-700 text-white",
              "disabled:opacity-50"
            )}
          >
            {isGeneratingSuggestions ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            {isGeneratingSuggestions ? "Generating…" : "Strategic Suggestions"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onRegenerate}
            className="gap-1.5 text-xs"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Update sources
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        {boardContextRecord?.contextHealth && (
          <ContextHealthBand markdown={boardContextRecord.contextHealth} />
        )}

        {sortedGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-muted-foreground">No context groups yet.</p>
          </div>
        ) : (
          sortedGroups.map((group) => (
            <GroupSection key={group.id} group={group} statements={statements} />
          ))
        )}
      </div>
    </div>
  );
};
